import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dropzone } from '@/components/ui/dropzone';
import { Progress } from '@/components/ui/progress';
import {
  checkDuplicate,
  computeFileChecksum,
  confirmUpload,
  getUploadUrl,
  uploadNewVersion,
  uploadToSignedUrl,
} from '../api';
import type { DuplicateMatch } from '../api';

interface UploadItem {
  id: string;
  fileName: string;
  status: 'hashing' | 'checking' | 'uploading' | 'confirming' | 'done' | 'error';
  progress: number;
  error?: string;
}

type DuplicateAction = 'skip' | 'replace';

interface DuplicatePrompt {
  fileName: string;
  duplicates: DuplicateMatch[];
}

interface UploadDropzoneProps {
  /** Called with the canonical document ID after a successful upload or duplicate match */
  onUploaded?: (documentId: string) => void;
}

export function UploadDropzone({ onUploaded }: UploadDropzoneProps = {}) {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [duplicatePrompt, setDuplicatePrompt] = useState<DuplicatePrompt | null>(null);
  const duplicateResolverRef = useRef<((action: DuplicateAction) => void) | null>(null);
  const sessionChecksumsRef = useRef<Set<string>>(new Set());
  const sessionFilesRef = useRef<Map<string, string>>(new Map());

  const updateUpload = useCallback((id: string, updates: Partial<UploadItem>) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...updates } : u)));
  }, []);

  const removeUpload = useCallback((id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const uploadId = crypto.randomUUID();
      setUploads((prev) => [...prev, { id: uploadId, fileName: file.name, status: 'hashing', progress: 5 }]);

      try {
        // Step 1: Compute checksum
        const checksum = await computeFileChecksum(file);
        updateUpload(uploadId, { status: 'checking', progress: 10 });

        // Step 2: Check duplicates (server + client-side session tracking)
        const { duplicates } = await checkDuplicate(checksum);
        const fileKey = `${file.name}|${file.size}`;
        const sessionDupByChecksum = sessionChecksumsRef.current.has(checksum);
        const sessionDupByName = sessionFilesRef.current.has(fileKey);
        const isSessionDup = sessionDupByChecksum || sessionDupByName;
        const allDuplicates = isSessionDup && duplicates.length === 0
          ? [{ documentId: '', documentName: sessionFilesRef.current.get(fileKey) ?? file.name, originalName: file.name, createdAt: new Date().toISOString() }]
          : duplicates;

        let replaceDocId: string | null = null;

        if (allDuplicates.length > 0) {
          // When onUploaded is set (e.g. invoice context), auto-link the existing doc — no blocking dialog
          if (onUploaded) {
            const existingDoc = allDuplicates.find((d) => d.documentId);
            if (existingDoc) {
              updateUpload(uploadId, { status: 'done', progress: 100 });
              setTimeout(() => removeUpload(uploadId), 3000);
              return { documentId: existingDoc.documentId, deduplicated: true };
            }
          }

          setDuplicatePrompt({ fileName: file.name, duplicates: allDuplicates });
          const action = await new Promise<DuplicateAction>((resolve) => {
            duplicateResolverRef.current = resolve;
          });
          setDuplicatePrompt(null);
          duplicateResolverRef.current = null;

          if (action === 'skip') {
            removeUpload(uploadId);
            return null;
          }
          // action === 'replace': upload as new version of the first matched document
          const targetDoc = allDuplicates.find((d) => d.documentId);
          if (targetDoc) {
            replaceDocId = targetDoc.documentId;
          }
        }

        updateUpload(uploadId, { status: 'uploading', progress: 25 });

        let documentId: string;
        let uploadUrl: string;

        const mimeType = file.type || 'application/octet-stream';

        if (replaceDocId) {
          // Replace: upload as new version of existing document
          const result = await uploadNewVersion(replaceDocId, file.name, mimeType);
          documentId = replaceDocId;
          uploadUrl = result.uploadUrl;
        } else {
          // New document
          const result = await getUploadUrl(file.name, mimeType, { uploadSource: 'ui' });
          documentId = result.documentId;
          uploadUrl = result.uploadUrl;
        }
        updateUpload(uploadId, { progress: 40 });

        // Upload to GCS
        await uploadToSignedUrl(uploadUrl, file);
        updateUpload(uploadId, { progress: 70 });

        // Confirm upload (triggers pipeline)
        updateUpload(uploadId, { status: 'confirming', progress: 85 });
        const confirmation = await confirmUpload(documentId, file.size, checksum);
        if (confirmation.status === 'failed') {
          throw new Error(confirmation.conversionError || 'Document processing failed');
        }
        sessionChecksumsRef.current.add(checksum);
        sessionFilesRef.current.set(fileKey, file.name);
        updateUpload(uploadId, { status: 'done', progress: 100 });

        setTimeout(() => removeUpload(uploadId), 3000);
        return confirmation;
      } catch (error) {
        updateUpload(uploadId, {
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        });
        throw error;
      }
    },
    onSuccess: (confirmation) => {
      if (confirmation) {
        queryClient.invalidateQueries({ queryKey: ['documents'] });
        onUploaded?.(confirmation.documentId);
        if (confirmation.deduplicated) {
          toast.info('Document already exists — linked the existing one');
        } else {
          toast.success('Document uploaded', {
            description: confirmation.status === 'ready'
              ? 'Your document is ready.'
              : 'Processing started — it will appear in the list shortly.',
          });
        }
      }
    },
    onError: (error) => {
      toast.error('Upload failed', { description: error instanceof Error ? error.message : 'Unknown error' });
    },
  });

  // Serialize uploads so duplicate dialogs don't stack
  const uploadQueueRef = useRef<File[]>([]);
  const processingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    while (uploadQueueRef.current.length > 0) {
      const file = uploadQueueRef.current.shift()!;
      try {
        await uploadMutation.mutateAsync(file);
      } catch {
        // Error already handled in mutation callbacks
      }
    }
    processingRef.current = false;
  }, [uploadMutation]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      uploadQueueRef.current.push(...acceptedFiles);
      processQueue();
    },
    [processQueue],
  );

  const activeUploads = uploads.filter((u) => u.status !== 'done');

  return (
    <>
      <Dropzone onFiles={onDrop} multiple />

      {activeUploads.length > 0 && (
        <div className="space-y-2 mt-3">
          {activeUploads.map((upload) => (
            <div key={upload.id} className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
              {upload.status === 'error' ? (
                <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
              ) : upload.status === 'done' ? (
                <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{upload.fileName}</p>
                {upload.error ? (
                  <p className="text-xs text-destructive">{upload.error}</p>
                ) : (
                  <Progress value={upload.progress} className="h-1 mt-1" />
                )}
              </div>
              {upload.status === 'error' && (
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeUpload(upload.id)}>
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={duplicatePrompt !== null} onOpenChange={() => duplicateResolverRef.current?.('skip')}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Identical file already exists</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  <strong>{duplicatePrompt?.fileName}</strong> is identical to{' '}
                  {duplicatePrompt?.duplicates.length === 1 ? 'an existing document' : `${duplicatePrompt?.duplicates.length} existing documents`}:
                </p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {duplicatePrompt?.duplicates.map((d) => (
                    <li key={d.documentId || d.originalName}>
                      {d.documentName || d.originalName}
                      {d.createdAt && (
                        <span className="text-muted-foreground ml-1">
                          ({new Date(d.createdAt).toLocaleDateString('en-CA')})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => duplicateResolverRef.current?.('skip')}>
              Skip
            </Button>
            {duplicatePrompt?.duplicates.some((d) => d.documentId) && (
              <Button variant="default" onClick={() => duplicateResolverRef.current?.('replace')}>
                Replace existing
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
